import {
  Cake,
  Calendar,
  Hash,
  Inbox,
  Pencil,
  UserX,
  UserCheck,
  Trash2,
  GraduationCap,
  Users,
} from "lucide-react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import swal from "../../utils/swal";
import { UPLOADS_URL } from "../../config/constants";
import { normalizeStringList } from "../../config/helpers/childHealth";
import { useUpdateChildStatus } from "../../query/childQuery";
import { DetailCoverHero } from "../../components/DetailCoverHero";
import { classroomColorTone } from "../../config/classroomColors";
import { SECTIONS } from "./HealthNotesFields";
import "../teachers/teacherDetails.css";
import "../classroom/classroom.css";
import "./children.css";

function initials(firstName = "", lastName = "") {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "S";
}

function fullName(child) {
  return `${child?.firstName || ""} ${child?.lastName || ""}`.trim() || "Student";
}

function parentName(parent) {
  if (!parent) return "";
  const father = `${parent.fatherFirstName || ""} ${parent.fatherLastName || ""}`.trim();
  const mother = `${parent.motherFirstName || ""} ${parent.motherLastName || ""}`.trim();
  return [father, mother].filter(Boolean).join(" / ");
}

function HealthDetailCard({ section, items }) {
  const list = normalizeStringList(items);
  const Icon = section.icon;
  const toneClass = section.tone.replace("is-", "");
  const isNote = section.name === "summary";

  return (
    <article className={`ch-detail-card is-${toneClass}${section.full ? " is-full" : ""}`}>
      <div className="ch-detail-card-head">
        <span className={`ch-panel-icon ${section.tone}`}>
          <Icon size={16} />
        </span>
        <strong>{section.label}</strong>
      </div>
      {!list.length ? (
        <em className="ch-detail-empty">None recorded</em>
      ) : isNote ? (
        <div className="ch-note-list">
          {list.map((item, index) => (
            <p key={`${item}-${index}`} className="ch-note-item">
              {item}
            </p>
          ))}
        </div>
      ) : (
        <div className="ch-detail-tags">
          {list.map((item, index) => (
            <span key={`${item}-${index}`} className="ch-detail-tag">
              {item}
            </span>
          ))}
        </div>
      )}
    </article>
  );
}

export default function ChildProfile({ child, onDelete }) {
  const navigate = useNavigate();
  const { mutate: updateChildStatus, isPending: isUpdatingStatus } = useUpdateChildStatus();

  const name = fullName(child);
  const isActive = child?.status === "ACTIVE";
  const statusLabel = isActive ? "Active" : "Inactive";
  const className = child?.classroom?.classroomName;
  const classTone = className ? classroomColorTone(child?.classroom?.color) : "is-muted";
  const parent = child?.parent;
  const ageLabel = child?.age
    ? `${child.age} yrs`
    : child?.birthday
    ? `${dayjs().diff(dayjs(child.birthday), "year")} yrs`
    : "—";

  const healthSections = SECTIONS.filter((section) => section.name !== "summary");
  const noteSection = SECTIONS.find((section) => section.name === "summary");
  const hasAnyHealth = SECTIONS.some((section) => normalizeStringList(child?.[section.name]).length);

  const handleStatus = () => {
    const nextStatus = isActive ? "INACTIVE" : "ACTIVE";
    swal({
      title: "Are you sure?",
      text: `Do you want to ${nextStatus === "ACTIVE" ? "activate" : "deactivate"} this student?`,
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
      dangerMode: true,
    }).then((isConfirm) => {
      if (!isConfirm) return;
      updateChildStatus(
        { childId: child._id, status: nextStatus },
        {
          onSuccess: () => swal("Success", "Status updated successfully", "success"),
          onError: (error) => swal("Error", error?.message || "Failed to update status", "error"),
        }
      );
    });
  };

  return (
    <div className="td-page">
      <div className="td-main">
        <article className="td-card">
          <DetailCoverHero
            coverKey={child?._id}
            backLabel="Back to students"
            onBack={() => navigate("/children")}
          />

          <div className="td-body">
            <div className="td-identity">
              {child?.image ? (
                <img className="td-avatar is-student" src={`${UPLOADS_URL}/${child.image}`} alt={name} />
              ) : (
                <span className="td-avatar is-student">{initials(child?.firstName, child?.lastName)}</span>
              )}
              <div className="td-identity-row">
                <h2>{name}</h2>
                <span className={`td-status is-${(child?.status || "inactive").toLowerCase()}`}>
                  {statusLabel}
                </span>
              </div>
              <p>{child?.rollNumber ? `Roll ${child.rollNumber} · Term ${child.term || "—"}` : "Student"}</p>
            </div>

            <div className="td-chips">
              <span>
                <i className="is-orange">
                  <Cake size={14} />
                </i>
                {child?.birthday ? dayjs(child.birthday).format("MMMM D, YYYY") : "Birthday not set"}
              </span>
              <span>
                <i className="is-orange">
                  <Hash size={14} />
                </i>
                Age {ageLabel}
              </span>
              <span>
                <i className={`${classTone}`}>
                  <GraduationCap size={14} />
                </i>
                {className || "No class assigned"}
              </span>
            </div>

            <section className="td-section ch-detail-section">
              <h3>Health & Notes</h3>
              {hasAnyHealth ? (
                <div className="ch-detail-board">
                  {healthSections.map((section) => (
                    <HealthDetailCard key={section.name} section={section} items={child?.[section.name]} />
                  ))}
                  {noteSection ? (
                    <HealthDetailCard section={noteSection} items={child?.summary} />
                  ) : null}
                </div>
              ) : (
                <div className="td-empty">
                  <Inbox size={22} />
                  <span>No health information or notes recorded</span>
                </div>
              )}
            </section>
          </div>
        </article>

        <div className="td-actions">
          <button
            type="button"
            className="td-action is-primary"
            onClick={() => navigate(`/children/edit/${child._id}`)}
          >
            <Pencil size={15} />
            Edit details
          </button>
          <button
            type="button"
            className="td-action"
            onClick={() => navigate(`/children/attendance/${child._id}`)}
          >
            <Calendar size={15} />
            Attendance
          </button>
          <button
            type="button"
            className="td-action"
            disabled={isUpdatingStatus}
            onClick={handleStatus}
          >
            {isActive ? <UserX size={15} /> : <UserCheck size={15} />}
            {isActive ? "Deactivate" : "Activate"}
          </button>
          <button type="button" className="td-action is-danger" onClick={onDelete}>
            <Trash2 size={15} />
            Delete
          </button>
        </div>
      </div>

      <aside className="td-schedule">
        <div className="td-schedule-head">
          <h3>Class</h3>
          <p>{className || "Not assigned"}</p>
        </div>
        {child?.classroom ? (
          <div className="td-schedule-list">
            <article className="td-lesson is-navy">
              <div className="td-lesson-copy">
                <strong>{child.classroom.classroomName}</strong>
                <p>{child.classroom.classroomId || "Section"}</p>
                <div className="td-lesson-meta">
                  <span>
                    {child.classroom.classroomGrade ? `Grade ${child.classroom.classroomGrade}` : "Classroom"}
                  </span>
                </div>
              </div>
            </article>
          </div>
        ) : (
          <div className="td-empty">
            <GraduationCap size={22} />
            <span>Assign a class from edit details</span>
          </div>
        )}

        <div className="td-schedule-head" style={{ marginTop: 24 }}>
          <h3>Parent</h3>
          <p>{parentName(parent) || "Not assigned"}</p>
        </div>
        {parent ? (
          <div className="td-schedule-list">
            <button
              type="button"
              className="td-lesson is-green"
              style={{ width: "100%", border: 0, cursor: "pointer", textAlign: "left" }}
              onClick={() => navigate(`/parents/${parent._id}`)}
            >
              <div className="td-lesson-copy">
                <strong>{parentName(parent)}</strong>
                <p>{parent.email || "Parent account"}</p>
                <div className="td-lesson-meta">
                  <Users size={14} />
                  <span>View parent profile</span>
                </div>
              </div>
            </button>
          </div>
        ) : (
          <div className="td-empty">
            <Users size={22} />
            <span>Assign a parent from edit details</span>
          </div>
        )}
      </aside>
    </div>
  );
}
